import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Menu, X } from "lucide-react";

const NAV = [
    { label: "Services", href: "/#services" },
    { label: "Subscribe", href: "/#subscribe" },
    { label: "Archive", href: "/archive" },
    { label: "Request Shoot", href: "/#request-shoot" },
    { label: "Trust & C2PA", href: "/#trust" },
    { label: "Portfolio", href: "https://trentonfranklin.com", external: true },
];

export default function Header() {
    const { user } = useAuth();
    const nav = useNavigate();
    const loc = useLocation();
    const [open, setOpen] = useState(false);

    const go = (href) => {
        setOpen(false);
        if (href.startsWith("http")) {
            window.open(href, "_blank", "noopener,noreferrer");
            return;
        }
        if (href.startsWith("/#")) {
            if (loc.pathname !== "/") {
                nav(href.replace("/#", "/"));
                setTimeout(() => {
                    const id = href.replace("/#", "");
                    const el = document.getElementById(id);
                    el?.scrollIntoView({ behavior: "smooth", block: "start" });
                }, 100);
            } else {
                const id = href.replace("/#", "");
                document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
            }
        } else {
            nav(href);
        }
    };

    return (
        <header
            data-testid="site-header"
            className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl"
            style={{ background: "rgba(10,11,14,0.85)", borderBottom: "1px solid var(--gt-border)" }}
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="h-16 flex items-center justify-between">
                    <Link to="/" data-testid="brand-link" className="flex items-center gap-3 group">
                        <span className="w-8 h-8 flex items-center justify-center" style={{ background: "var(--gt-accent)" }}>
                            <i className="fa-solid fa-camera-retro" style={{ color: "#1a0f0a", fontSize: 13 }}></i>
                        </span>
                        <div className="flex flex-col leading-tight">
                            <span className="font-display text-lg" style={{ color: "var(--gt-text)" }}>First Landing Studio</span>
                            <span className="overline" style={{ fontSize: 9 }}>Trenton Franklin · Guatemala</span>
                        </div>
                    </Link>

                    <nav className="hidden lg:flex items-center gap-7">
                        {NAV.map((n) => (
                            <button
                                key={n.href}
                                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                                onClick={() => go(n.href)}
                                className="text-sm transition-colors"
                                style={{ color: "var(--gt-text-2)" }}
                                onMouseEnter={(e) => (e.currentTarget.style.color = "var(--gt-text)")}
                                onMouseLeave={(e) => (e.currentTarget.style.color = "var(--gt-text-2)")}
                            >
                                {n.label}
                            </button>
                        ))}
                    </nav>

                    <div className="flex items-center gap-3">
                        {user ? (
                            <button
                                data-testid="header-dashboard-btn"
                                onClick={() => nav("/dashboard")}
                                className="gt-btn-primary px-4 py-2 text-sm hidden sm:inline-flex"
                            >
                                Owner Portal
                            </button>
                        ) : (
                            <button
                                data-testid="header-login-btn"
                                onClick={() => nav("/login")}
                                className="gt-btn-ghost px-4 py-2 text-sm hidden sm:inline-flex"
                            >
                                Owner Portal
                            </button>
                        )}
                        <button
                            data-testid="header-cta-request"
                            onClick={() => go("/#request-shoot")}
                            className="gt-btn-primary px-4 py-2 text-sm hidden md:inline-flex"
                        >
                            Commission a Shoot
                        </button>
                        <button
                            data-testid="header-mobile-toggle"
                            onClick={() => setOpen((v) => !v)}
                            className="lg:hidden gt-btn-ghost p-2"
                            aria-label="Toggle menu"
                        >
                            {open ? <X size={16} /> : <Menu size={16} />}
                        </button>
                    </div>
                </div>

                {open && (
                    <div data-testid="mobile-menu" className="lg:hidden pb-4 flex flex-col gap-2 border-t" style={{ borderColor: "var(--gt-border)" }}>
                        {NAV.map((n) => (
                            <button
                                key={n.href}
                                onClick={() => go(n.href)}
                                className="text-left py-2 text-sm"
                                style={{ color: "var(--gt-text)" }}
                            >
                                {n.label}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </header>
    );
}
